import { useState, Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Pagination, PaginationContent, PaginationItem, PaginationPrevious, PaginationLink, PaginationNext } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  Search,
  UserPlus,
  Eye,
  Pencil,
  Trash,
  MoreHorizontal,
  Filter,
  X,
  RefreshCw,
  Mail,
  MapPin,
  Shield,
  User,
  LayoutGrid,
  AlertCircle
} from "lucide-react";
import { RootState } from "@/store";
import { fetchFilteredUsers, setUserFilter, resetUserFilter } from "@/features/user/userSlice";
import { UserFilter } from "@/features/user/types";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
const UsersEnhanced = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { usersList, paginationMeta, usersLoading, usersError, filter } = useSelector(
    (state: RootState) => state.user
  );

  // Fetch users on component mount
  useEffect(() => {
    // Only fetch if the list is empty
    if (!usersList || usersList.length === 0) {
      dispatch(fetchFilteredUsers({
        page: 1,
        limit: 10
      } as UserFilter) as any);
    }
  }, [dispatch, usersList]);

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeView, setActiveView] = useState("table");

  // Predefined list of locations
  const locations = ["New York", "London", "Tokyo", "Paris", "Tunis", "Sfax", "Sousse"];

  // Search handler
  const handleSearch = () => {
    dispatch(setUserFilter({ ...filter, search: searchTerm, page: 1 }));
    dispatch(fetchFilteredUsers({ ...filter, search: searchTerm, page: 1 } as UserFilter) as any);
  };

  // Role filter handler
  const handleRoleFilter = (value: string) => {
    setSelectedRole(value);
    const roles = value === "ALL" ? [] : [value.toLowerCase()];
    dispatch(setUserFilter({ ...filter, roles, page: 1 }));
    dispatch(fetchFilteredUsers({ ...filter, roles, page: 1 } as UserFilter) as any);
  };

  // Location filter handler
  const handleLocationChange = (value: string) => {
    let updatedLocations: string[];

    if (value === "ALL") {
      updatedLocations = [];
    } else if (selectedLocations.includes(value)) {
      updatedLocations = selectedLocations.filter(loc => loc !== value);
    } else {
      updatedLocations = [...selectedLocations, value];
    }

    setSelectedLocations(updatedLocations);

    const locationString = updatedLocations.length > 0 ? updatedLocations.join(',') : '';
    dispatch(setUserFilter({ ...filter, location: locationString, page: 1 }));
    dispatch(fetchFilteredUsers({ ...filter, location: locationString, page: 1 } as UserFilter) as any);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedRole("");
    setSelectedLocations([]);
    setShowFilters(false);
    dispatch(resetUserFilter());
    dispatch(
      fetchFilteredUsers({
        search: "", // Changed from null to empty string
        roles: [],
        location: "",
        page: 1,
        limit: 10,
      } as UserFilter) as any
    );
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    dispatch(setUserFilter({ ...filter, page }));
    dispatch(fetchFilteredUsers({ ...filter, page } as UserFilter) as any);
  };

  // Utility to get initials
  const getInitials = (name: string | null) => {
    if (!name) return "";
    const names = name.split(" ");
    return names.length > 1
      ? `${names[0][0]}${names[1][0]}`
      : name.substring(0, 2).toUpperCase();
  };

  // Role badge color
  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case "admin":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "instructor":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "user":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // User action handlers
  const handleViewProfile = (userId: string | number) => {
    toast({
      title: "View Profile",
      description: `Navigating to user profile ${userId}`,
      duration: 2000
    });
    // Navigate to user profile
    navigate(`/users/${userId}`);
  };

  const handleEdit = (user: any) => {
    toast({
      title: "Edit User",
      description: `Editing user ${user.username || user.email}`,
      duration: 2000
    });
    // Open edit modal
  };

  const handleDeleteClick = (user: any) => {
    toast({
      title: "Delete User",
      description: `This would delete user ${user.username || user.email}`,
      variant: "destructive",
      duration: 3000
    });
    // Show confirmation dialog
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users Management</h1>
          <p className="text-muted-foreground mt-1">View, filter, and manage user accounts</p>
        </div>
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5"
          >
            {showFilters ? <X className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
            {showFilters ? "Hide Filters" : "Filters"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="flex items-center gap-1.5 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary shadow-sm transition-all duration-300 hover:shadow-md"
                size="sm"
              >
                <UserPlus className="h-4 w-4" />
                Add User
                <svg className="h-4 w-4 ml-1 opacity-70" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background border border-border/50 shadow-lg rounded-lg w-56 p-1 animate-in fade-in-80 zoom-in-95">
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground px-2 py-1.5">User Types</DropdownMenuLabel>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem
                className="flex items-center cursor-pointer rounded-md hover:bg-primary/10 focus:bg-primary/10 px-3 py-2 text-sm transition-colors"
                onClick={() => navigate('/admin/users/create-provider')}
              >
                <div className="flex items-center justify-center bg-primary/10 p-2 rounded-md mr-3">
                  <Shield className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Provider User</p>
                  <p className="text-xs text-muted-foreground">Create a user with provider privileges</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center cursor-pointer rounded-md hover:bg-primary/10 focus:bg-primary/10 px-3 py-2 text-sm transition-colors"
                onClick={() => toast({
                  title: "Coming Soon",
                  description: "This feature will be available soon.",
                  duration: 3000
                })}
              >
                <div className="flex items-center justify-center bg-blue-100 p-2 rounded-md mr-3">
                  <svg className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Instructor</p>
                  <p className="text-xs text-muted-foreground">Create a user with instructor privileges</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center cursor-pointer rounded-md hover:bg-primary/10 focus:bg-primary/10 px-3 py-2 text-sm transition-colors"
                onClick={() => toast({
                  title: "Coming Soon",
                  description: "This feature will be available soon.",
                  duration: 3000
                })}
              >
                <div className="flex items-center justify-center bg-green-100 p-2 rounded-md mr-3">
                  <User className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Standard User</p>
                  <p className="text-xs text-muted-foreground">Create a regular user account</p>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* View Switcher and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search users by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-9 w-full"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSearch} size="sm" className="flex items-center gap-1.5">
            <Search className="h-4 w-4" />
            Search
          </Button>

          <Button
            onClick={resetFilters}
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <Card className="border border-border/40 shadow-sm animate-fadeIn">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Role Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Role</label>
                <Select value={selectedRole} onValueChange={handleRoleFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent className="bg-background z-50">
                    <SelectItem value="ALL">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Location Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Location</label>
                <Select
                  value={
                    selectedLocations.length > 0
                      ? selectedLocations[selectedLocations.length - 1]
                      : "ALL"
                  }
                  onValueChange={handleLocationChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent className="bg-background z-50 max-h-[200px]">
                    <SelectItem value="ALL">All Locations</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location} value={location}>
                        <div className="flex items-center justify-between w-full">
                          <span>{location}</span>
                          {selectedLocations.includes(location) && (
                            <svg className="h-3.5 w-3.5 text-primary ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters */}
            {(selectedRole || selectedLocations.length > 0) && (
              <div className="mt-4 pt-4 border-t border-border/40">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm font-medium text-muted-foreground">Active filters:</span>

                  {selectedRole && selectedRole !== "ALL" && (
                    <Badge
                      variant="outline"
                      className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10"
                    >
                      Role: {selectedRole}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => handleRoleFilter("ALL")}
                      />
                    </Badge>
                  )}

                  {selectedLocations.map((location) => (
                    <Badge
                      key={location}
                      variant="outline"
                      className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10"
                    >
                      <MapPin className="h-3 w-3" />
                      {location}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => handleLocationChange(location)}
                      />
                    </Badge>
                  ))}

                  {(selectedRole || selectedLocations.length > 0) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetFilters}
                      className="h-7 px-2 text-xs"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card className="border border-border/40 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <CardTitle>All Users ({paginationMeta?.totalItems || 0})</CardTitle>
              <CardDescription>
                {paginationMeta && `Showing ${paginationMeta.itemCount} of ${paginationMeta.totalItems} users`}
              </CardDescription>
            </div>

            <Tabs value={activeView} onValueChange={setActiveView} className="w-full sm:w-auto">
              <TabsList className="grid w-full sm:w-auto grid-cols-2">
                <TabsTrigger value="table" className="flex items-center gap-1.5">
                  <User className="h-4 w-4" />
                  Table
                </TabsTrigger>
                <TabsTrigger value="cards" className="flex items-center gap-1.5">
                  <LayoutGrid className="h-4 w-4" />
                  Cards
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {usersError && (
            <div className="p-4 mb-4 mx-4 text-destructive bg-destructive/10 rounded-md flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Error loading users: {usersError}
            </div>
          )}

          {activeView === "table" ? (
            <div className="rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {usersLoading ? (
                    // Loading skeletons
                    Array(5).fill(0).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <Skeleton className="h-4 w-24" />
                          </div>
                        </TableCell>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-8 w-8 rounded-full ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : usersList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <User className="h-8 w-8 mb-2 opacity-40" />
                          <p>No users found</p>
                          <p className="text-sm">Try adjusting your filters or search terms</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    usersList.map((user: any) => (
                      <TableRow key={user.id} className="hover:bg-muted/40 transition-colors">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9 border border-border/50">
                              <AvatarImage src={user.profilePhoto || undefined} />
                              <AvatarFallback className="text-xs">{getInitials(user.username)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">{user.username || user.email.split("@")[0]}</div>
                              {user.bio && <div className="text-xs text-muted-foreground line-clamp-1">{user.bio}</div>}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="link"
                            className="p-0 h-auto font-normal text-muted-foreground hover:text-foreground flex items-center gap-1"
                            onClick={() => window.open(`mailto:${user.email}`)}
                          >
                            <Mail className="h-3.5 w-3.5 opacity-70" />
                            {user.email}
                          </Button>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.roles.map((role: string, index: number) => (
                              <Badge key={index} variant="outline" className={getRoleBadgeColor(role)}>
                                <div className="flex items-center">
                                  {role.toLowerCase() === "admin" && <Shield className="h-3 w-3 mr-1" />}
                                  {role}
                                </div>
                              </Badge>
                            ))}
                          </div>
                        </TableCell>

                        <TableCell>
                          {user.location ? (
                            <div className="flex items-center text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 mr-1 opacity-70" />
                              {user.location}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Not specified</span>
                          )}
                        </TableCell>

                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-background border border-border/50 shadow-md">
                              <DropdownMenuItem
                                className="flex items-center cursor-pointer"
                                onClick={() => handleViewProfile(user.id)}
                              >
                                <Eye className="h-4 w-4 mr-2 opacity-70" />
                                View Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="flex items-center cursor-pointer"
                                onClick={() => handleEdit(user)}
                              >
                                <Pencil className="h-4 w-4 mr-2 opacity-70" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(user)}
                                className="text-destructive flex items-center cursor-pointer"
                              >
                                <Trash className="h-4 w-4 mr-2 opacity-70" />
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            // Card View
            <div className="p-4">
              {usersLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array(6).fill(0).map((_, index) => (
                    <Card key={index} className="overflow-hidden border border-border/40 relative">
                      {/* Status indicator skeleton */}
                      <div className="absolute top-3 right-3 h-2.5 w-2.5 rounded-full bg-muted animate-pulse"></div>

                      <CardContent className="p-0">
                        {/* Card Header with Avatar and Name */}
                        <div className="p-5 pb-3">
                          <div className="flex items-start gap-4">
                            <Skeleton className="h-14 w-14 rounded-xl flex-shrink-0" />
                            <div className="flex-1 pt-1 space-y-3">
                              <Skeleton className="h-6 w-3/5" />
                              <div className="flex gap-2">
                                <Skeleton className="h-5 w-16 rounded-full" />
                                <Skeleton className="h-5 w-20 rounded-full" />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Card Body with Contact Info */}
                        <div className="px-5 pb-3 space-y-3">
                          <div className="flex items-center">
                            <Skeleton className="h-4 w-4 mr-2 rounded-full" />
                            <Skeleton className="h-4 w-40" />
                          </div>

                          <div className="flex items-center">
                            <Skeleton className="h-4 w-4 mr-2 rounded-full" />
                            <Skeleton className="h-4 w-24" />
                          </div>
                        </div>

                        {/* Bio Section Skeleton (random display) */}
                        {index % 2 === 0 && (
                          <div className="px-5 pb-4">
                            <div className="p-3 bg-muted/20 rounded-lg border border-border/30">
                              <Skeleton className="h-4 w-full mb-1" />
                              <Skeleton className="h-4 w-4/5" />
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="px-5 py-3 bg-muted/20 border-t border-border/40 flex justify-between">
                          <Skeleton className="h-9 w-20" />
                          <div className="flex gap-1">
                            <Skeleton className="h-9 w-9 rounded" />
                            <Skeleton className="h-9 w-9 rounded" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : usersList.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <div className="max-w-md mx-auto">
                    <div className="bg-muted/30 rounded-full h-20 w-20 flex items-center justify-center mx-auto mb-6">
                      <User className="h-10 w-10 text-muted-foreground opacity-70" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">No users found</h3>
                    <p className="text-muted-foreground mb-6 max-w-sm mx-auto">We couldn't find any users matching your current filters or search terms.</p>
                    <Button
                      variant="outline"
                      onClick={resetFilters}
                      className="mx-auto flex items-center gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Reset Filters
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {usersList.map((user: any) => (
                    <Card
                      key={user.id}
                      className="overflow-hidden border border-border/40 hover:border-primary/20 hover:shadow-md transition-all group relative"
                    >
                      {/* Status indicator - could be based on user.status if available */}
                      <div className="absolute top-3 right-3 h-2.5 w-2.5 rounded-full bg-green-500 shadow-sm shadow-green-500/50"></div>

                      <CardContent className="p-0">
                        {/* Card Header with Avatar and Name */}
                        <div className="p-5 pb-3">
                          <div className="flex items-start gap-4">
                            <Avatar className="h-14 w-14 border-2 border-border/50 rounded-xl shadow-sm group-hover:border-primary/30 transition-colors">
                              <AvatarImage src={user.profilePhoto || undefined} />
                              <AvatarFallback className="text-base bg-primary/5 font-medium">
                                {getInitials(user.username)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 pt-1">
                              <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">
                                {user.username || user.email.split("@")[0]}
                              </h3>

                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {user.roles.map((role: string, index: number) => (
                                  <Badge
                                    key={index}
                                    variant="outline"
                                    className={`${getRoleBadgeColor(role)} text-xs py-0 px-2`}
                                  >
                                    <div className="flex items-center">
                                      {role.toLowerCase() === "admin" && <Shield className="h-3 w-3 mr-1" />}
                                      {role.toLowerCase() === "instructor" &&
                                        <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M12 14l9-5-9-5-9 5 9 5z" />
                                          <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                        </svg>
                                      }
                                      {role.toLowerCase() === "user" &&
                                        <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                      }
                                      {role}
                                    </div>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Card Body with Contact Info */}
                        <div className="px-5 pb-3 space-y-2">
                          <div className="flex items-center text-muted-foreground hover:text-foreground transition-colors">
                            <Mail className="h-4 w-4 mr-2 opacity-70" />
                            <Button
                              variant="link"
                              className="p-0 h-auto font-normal text-inherit flex items-center gap-1 text-sm"
                              onClick={() => window.open(`mailto:${user.email}`)}
                            >
                              {user.email}
                            </Button>
                          </div>

                          {user.location && (
                            <div className="flex items-center text-muted-foreground">
                              <MapPin className="h-4 w-4 mr-2 opacity-70" />
                              <span className="text-sm">{user.location}</span>
                            </div>
                          )}

                          {user.phone && (
                            <div className="flex items-center text-muted-foreground">
                              <svg className="h-4 w-4 mr-2 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span className="text-sm">{user.phone}</span>
                            </div>
                          )}
                        </div>

                        {/* Bio Section */}
                        {user.bio && (
                          <div className="px-5 pb-4">
                            <div className="p-3 bg-muted/30 rounded-lg border border-border/40">
                              <p className="text-sm text-muted-foreground line-clamp-2 italic">"{user.bio}"</p>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="px-5 py-3 bg-muted/20 border-t border-border/40 flex justify-between">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 hover:bg-background hover:text-primary transition-colors"
                            onClick={() => handleViewProfile(user.id)}
                          >
                            <Eye className="h-4 w-4 mr-1.5" />
                            View
                          </Button>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9 hover:bg-background hover:text-primary transition-colors"
                              onClick={() => handleEdit(user)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9 hover:bg-background hover:text-destructive transition-colors"
                              onClick={() => handleDeleteClick(user)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {paginationMeta && paginationMeta.totalPages > 0 && (
          <div className="p-4 border-t border-border/40 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page:</span>
              <Select
                value={filter.limit?.toString() || "10"}
                onValueChange={(value) => {
                  const limit = parseInt(value);
                  dispatch(setUserFilter({ ...filter, limit, page: 1 }));
                  dispatch(
                    fetchFilteredUsers({ ...filter, limit, page: 1 } as UserFilter) as any
                  );
                }}
              >
                <SelectTrigger className="w-[80px] h-8">
                  <SelectValue placeholder="10" />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => handlePageChange(paginationMeta.currentPage - 1)}
                    className={
                      paginationMeta.currentPage <= 1
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                  />
                </PaginationItem>

                {Array.from({ length: paginationMeta.totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    const currentPage = paginationMeta.currentPage;
                    return (
                      page === 1 ||
                      page === paginationMeta.totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    );
                  })
                  .map((page, i, array) => {
                    if (i > 0 && array[i - 1] !== page - 1) {
                      return (
                        <Fragment key={`ellipsis-${page}`}>
                          <PaginationItem>
                            <span className="flex h-9 w-9 items-center justify-center">
                              ...
                            </span>
                          </PaginationItem>
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => handlePageChange(page)}
                              isActive={page === paginationMeta.currentPage}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        </Fragment>
                      );
                    }
                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => handlePageChange(page)}
                          isActive={page === paginationMeta.currentPage}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(paginationMeta.currentPage + 1)}
                    className={
                      paginationMeta.currentPage >= paginationMeta.totalPages
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UsersEnhanced;
